// src/App.js
import { useEffect, useRef } from "react";
import "./index.css";
import { Navigate, Routes, Route, useLocation } from "react-router-dom";

import { LanguageProvider } from "./contexts/LanguageContext";
import { AuthProvider } from './contexts/AuthContext';
import { useAuth } from "./contexts/AuthContext";
import {
  AnalyticsConsentProvider,
  useAnalyticsConsent,
} from "./contexts/AnalyticsConsentContext";
import AnalyticsConsentBanner from "./components/AnalyticsConsentBanner";
import Header from "./components/Header";
import PrivateRoute from "./components/PrivateRoute";
import RouteScrollManager from "./components/RouteScrollManager";
import NotFoundView from "./pages/Informational/NotFoundView";
import WebLandingView from "./pages/WebLandingView";
import DeckView from "./pages/DeckView";
import CustomizeQuestionsView from "./pages/Customize/CustomizeQuestionsView";
import CustomizeIntroView from "./pages/Customize/CustomizeIntroView";
import StoryView from "./pages/Informational/StoryView";
import CustomizeResultView from "./pages/Customize/CustomizeResultView";
import PrivacyPolicyView from "./pages/Informational/PrivacyPolicyView";
import TermsServiceView from "./pages/Informational/TermsServiceView";
import {
  getAnalyticsUserState,
  trackMixpanelEvent,
  trackPageView,
} from "./utils/Analytics";
import CustomizeHomeView from "./pages/Customize/CustomizeHomeView";
import AppLandingView from "./pages/AppLandingView";
import PurchaseSuccessView from "./pages/PurchaseSuccessView";
import MCAADView from "./pages/Partners/MCAADView";
import MCAADDemoView from "./pages/Partners/MCAADDemoView";
import RefineView from "./pages/EmpathyAI/RefineView";
import ResultView from "./pages/EmpathyAI/ResultView";
import WallView from "./pages/EmpathyAI/WallView";
import WallsView from "./pages/EmpathyAI/WallsView";
import SharedCardView from "./pages/EmpathyAI/SharedCardView";
import YourCardsView from "./pages/EmpathyAI/YourCardsView";
import { DEFAULT_WALL_PATH } from "./utils/cards/cardConstants";

function getExperienceForLocation(location) {
  const searchParams = new URLSearchParams(location.search);
  const hasShareAttribution = Boolean(searchParams.get("shareId"));
  const hasPartnerContext = searchParams.get("partner") === "mcaad";

  if (location.pathname === "/") {
    return { experience: "web_landing", entryPoint: "direct" };
  }

  if (location.pathname === "/mcaad" || location.pathname === "/mcaad-demo") {
    return {
      experience: "mcaad_landing",
      entryPoint: "partner_wall",
      partnerId: "mcaad",
    };
  }

  if (location.pathname === "/app-landing") {
    return { experience: "app_landing", entryPoint: "direct" };
  }

  if (location.pathname === "/refine" || location.pathname === "/create") {
    return {
      experience: "card_creator",
      entryPoint: hasShareAttribution
        ? "friend_share"
        : hasPartnerContext
          ? "partner_wall"
          : "direct",
      partnerId: hasPartnerContext ? "mcaad" : undefined,
    };
  }

  return null;
}

function isKnownRoute(pathname) {
  return (
    pathname === "/" ||
    pathname === "/story" ||
    pathname === "/app-landing" ||
    pathname === "/mcaad" ||
    pathname === "/mcaad-demo" ||
    pathname === "/refine" ||
    pathname === "/create" ||
    pathname === "/refine/loading-test" ||
    /^\/result\/[^/]+$/.test(pathname) ||
    pathname === "/your-cards" ||
    pathname === "/community-walls" ||
    pathname === "/wall" ||
    /^\/wall\/[^/]+$/.test(pathname) ||
    /^\/card\/[^/]+$/.test(pathname) ||
    pathname === "/home" ||
    pathname === "/customize/intro" ||
    /^\/deck\/[^/]+$/.test(pathname) ||
    pathname === "/customize/results" ||
    pathname === "/customize/questions" ||
    pathname === "/purchase/success" ||
    pathname === "/privacy" ||
    pathname === "/terms"
  );
}

function AnalyticsRouteTracker() {
  const location = useLocation();
  const { currentUser } = useAuth();
  const { analyticsReady } = useAnalyticsConsent();
  const trackedPathRef = useRef(null);
  const locationRef = useRef(location);
  const currentUserRef = useRef(currentUser);

  locationRef.current = location;
  currentUserRef.current = currentUser;

  const routeVisitKey = location.key || `${location.pathname}${location.search}`;
  const routePath = String(location.pathname || "/").split("?")[0];

  useEffect(() => {
    if (!analyticsReady) return;

    if (trackedPathRef.current === routeVisitKey) return;

    trackedPathRef.current = routeVisitKey;
    trackPageView(routePath);

    const experience = getExperienceForLocation(locationRef.current);
    if (!experience) return;

    trackMixpanelEvent("Experience Viewed", {
      experience: experience.experience,
      entry_point: experience.entryPoint,
      partner_id: experience.partnerId,
      user_state: getAnalyticsUserState(currentUserRef.current),
      language: navigator.language,
    });
  }, [analyticsReady, routePath, routeVisitKey]);

  return null;
}

function App() {
  const location = useLocation();

  // Hide Header on /app-landing
  const hideHeader = !isKnownRoute(location.pathname) || location.pathname === "/" || location.pathname === "/app-landing" || location.pathname === "/mcaad-demo" || location.pathname === "/mcaad" || location.pathname === "/refine" || location.pathname === "/create" || location.pathname.startsWith("/refine/") || location.pathname.startsWith("/result/") || location.pathname === "/your-cards" || location.pathname === "/community-walls" || location.pathname === "/wall" || location.pathname.startsWith("/wall/") || location.pathname.startsWith("/card/");

  return (
    <AnalyticsConsentProvider>
      <LanguageProvider>
        <AuthProvider>
          <RouteScrollManager />
          <AnalyticsRouteTracker />
          <div className="font-sans">
            {!hideHeader && <Header />}
            <main>
              <Routes>
              <Route path="/" element={<WebLandingView />} />
              <Route path="/story" element={<StoryView />} />
              <Route path="/app-landing" element={<AppLandingView />} />
              <Route path="/mcaad" element={<MCAADView />} />
              <Route path="/mcaad-demo" element={<MCAADDemoView />} />
              <Route path="/refine" element={<RefineView />} />
              <Route path="/create" element={<RefineView />} />
              <Route
                path="/refine/loading-test"
                element={<RefineView forceLoadingScreen />}
              />
              <Route path="/result/:generationId" element={<ResultView />} />
              <Route path="/your-cards" element={<YourCardsView />} />
              <Route path="/community-walls" element={<WallsView />} />
              <Route
                path="/wall"
                element={<Navigate to={DEFAULT_WALL_PATH} replace />}
              />
              <Route path="/wall/:wallId" element={<WallView />} />
              <Route path="/card/:cardId" element={<SharedCardView />} />

              {/* Protected routes that require authentication */}
              <Route
                path="/home"
                element={
                  <PrivateRoute>
                    <CustomizeHomeView />
                  </PrivateRoute>
                }
              />
              <Route
                path="/customize/intro"
                element={
                  <PrivateRoute>
                    <CustomizeIntroView />
                  </PrivateRoute>
                }
              />
              <Route
                path="/deck/:deckId"
                element={
                  <PrivateRoute>
                    <DeckView />
                  </PrivateRoute>
                }
              />
              <Route
                path="/customize/results"
                element={
                  <PrivateRoute>
                    <CustomizeResultView />
                  </PrivateRoute>
                }
              />
              <Route
                path="/customize/questions"
                element={
                  <PrivateRoute>
                    <CustomizeQuestionsView />
                  </PrivateRoute>
                }
              />
              <Route
                path="/purchase/success"
                element={
                  <PrivateRoute>
                    <PurchaseSuccessView />
                  </PrivateRoute>
                }
              />

              <Route path="/privacy" element={<PrivacyPolicyView />} />
              <Route path="/terms" element={<TermsServiceView />} />
              <Route path="*" element={<NotFoundView />} />
              </Routes>
            </main>
          </div>
          <AnalyticsConsentBanner />
        </AuthProvider>
      </LanguageProvider>
    </AnalyticsConsentProvider>
  );
};

export default App;
